import React from 'react';
import { shape, string } from 'prop-types';
import { useField } from 'informed';
import { FormFieldset, FormLegend } from '@tesla/design-system-react';
import _merge from 'lodash/merge';
import { useIntl } from 'react-intl';
import messages from './messages';
import { getDefaultErrorMessages } from './variables/errorMessages';
import { getDefaultLabels } from './variables/labels';
import { showSingleAddressLine } from './variables/singleAddressLine';
import { hideZipCode, isZipCodeOptional } from './variables/zipCode';
import { AddressLineInput, CityInput, CountryInput, StateSelect, ZipCodeInput } from './Inputs';
import { notEmpty } from './variables/patterns';

const Address = ({
  errorMessages: translatedErrorMessages,
  labels: translatedLabels,
  ...props
}) => {
  const intl = useIntl();
  const defaultValues = {
    address1: '',
    address2: '',
    city: '',
    countryCode: '',
    stateProvince: '',
    zipCode: '',
    test: '',
  };
  const { formatMessage, locale } = intl;
  const langCode = locale.substring(0, 2);
  const { defaultHandler, formFieldApi, formFieldState, userProps } = useField({
    ...props,
  });
  const name = defaultHandler.name.replace(/^\/+/, '');
  const { label, ...fieldProps } = userProps;
  const value = _merge(defaultValues, formFieldState.value);
  const { setValue, getValue } = formFieldApi;
  const errorMessages = _merge(getDefaultErrorMessages(intl), translatedErrorMessages);
  const labels = _merge(getDefaultLabels(intl, value.countryCode), translatedLabels);

  return (
    <FormFieldset name="address" variant="object" {...fieldProps}>
      {label && <FormLegend text={label} variant="h5" />}
      <div className="tds-form-layout--multicolumn">
        {/**
         * We trigger the onChange event here manually to ensure the component gets re-rendered. Some fields are
         * dependent on the country code (e.g. state field).
         */}
        <CountryInput
          name={`${name}.countryCode`}
          label={labels.country}
          langCode={langCode}
          required
          emptySelectOption={formatMessage(messages.EmptySelectOption)}
          onChange={(e) => setValue({ ...getValue(), countryCode: e.value })}
        />
      </div>
      <div className="tds-form-layout--multicolumn">
        <AddressLineInput
          name={`${name}.address1`}
          label={labels.addressLine1}
          required
          pattern={notEmpty}
        />
        {!showSingleAddressLine(value.countryCode) && (
          <AddressLineInput name={`${name}.address2`} label={labels.addressLine2} />
        )}
      </div>
      <div className="tds-form-layout--multicolumn">
        <CityInput country={value.countryCode} name={`${name}.city`} label={labels.city} required />
      </div>
      <div className="tds-form-layout--multicolumn">
        <StateSelect
          country={value.countryCode}
          name={`${name}.stateProvince`}
          label={labels.stateProvince}
          emptySelectOption={formatMessage(messages.EmptySelectOption)}
          required
        />
        {!hideZipCode(value.countryCode) && (
          <ZipCodeInput
            country={value.countryCode}
            name={`${name}.zipCode`}
            label={labels.postalCode}
            required={!isZipCodeOptional(value.countryCode)}
            pattern={notEmpty}
            errorMessage={{
              invalidPostalCode: errorMessages.invalidPostalCode,
            }}
          />
        )}
      </div>
    </FormFieldset>
  );
};

Address.propTypes = {
  errorMessages: shape({
    isRequired: string,
    invalidPostalCode: string,
  }),
  labels: shape({
    addressLine: string,
    addressLine1: string,
    addressLine2: string,
    city: string,
    country: string,
    postalCode: string,
    stateProvince: string,
  }),
};

Address.defaultProps = {
  errorMessages: {},
  labels: {},
};

export default Address;
